import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { bbFetch, fmtDate, Eyebrow, fieldCls, btnOrange, btnOutline, btnDanger, LevelBadge } from './bbShared';

const H = ({ children }) => <DialogTitle asChild><h2 className="text-2xl font-bold uppercase" style={{ fontFamily: 'var(--font-display)' }}>{children}</h2></DialogTitle>;

export const SortirDialog = ({ open, onClose, token, dossier, onDone }) => {
  const [issue, setIssue] = useState('');
  const [motif, setMotif] = useState('');
  useEffect(() => { if (open) { setIssue(''); setMotif(''); } }, [open]);
  const needMotif = issue === 'perdu' || issue === 'retire';
  const submit = async () => {
    try { await bbFetch(token, `/bb/dossiers/${dossier.id}/sortir`, { method: 'POST', body: JSON.stringify({ issue, motif: motif || null }) }); toast.success('Dossier sorti du bouillant'); onDone(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="bb-sortir-dialog">
        <H>Clôturer le dossier</H>
        <p className="text-sm text-slate-600">Quelle est l'issue du dossier {dossier?.sim?.compte} ?</p>
        <div className="grid grid-cols-3 gap-2">
          {[['signe', 'Signé'], ['perdu', 'Perdu'], ['retire', 'Retiré']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setIssue(k)} data-testid={`bb-issue-${k}`}
              className={`rounded-xl border-2 py-3 text-sm font-bold min-h-[44px] ${issue === k ? 'border-[#25272B] bg-[#F3F2EE]' : 'border-[#E5E3DE]'}`}>{l}</button>
          ))}
        </div>
        {issue && <div><p className="text-xs text-slate-500 mb-1">Motif {needMotif ? <b className="text-[#A1281D]">(obligatoire)</b> : '(facultatif)'}</p>
          <textarea rows={3} className={fieldCls} value={motif} onChange={e => setMotif(e.target.value)} data-testid="bb-sortir-motif" /></div>}
        <p className="text-xs text-slate-500">Le dossier quitte la liste active mais reste compté dans les mouvements et l'analyse des motifs.</p>
        <div className="flex justify-end gap-2"><button type="button" className={btnOutline} onClick={onClose}>Annuler</button>
          <button type="button" className={btnOrange} disabled={!issue || (needMotif && !motif.trim())} onClick={submit} data-testid="bb-sortir-submit">Valider la sortie</button></div>
      </DialogContent>
    </Dialog>
  );
};

const RAISONS = ['Doublon', 'Erreur de saisie', 'Simulation annulée', 'Autre'];

export const SupprimerDialog = ({ open, onClose, token, dossier, onDone, onSortir }) => {
  const [raison, setRaison] = useState('');
  const [comment, setComment] = useState('');
  useEffect(() => { if (open) { setRaison(''); setComment(''); } }, [open]);
  const submit = async () => {
    try { await bbFetch(token, `/bb/dossiers/${dossier.id}/supprimer`, { method: 'POST', body: JSON.stringify({ raison, commentaire: comment || null }) }); toast.success('Dossier placé dans la corbeille'); onDone(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="bb-supprimer-dialog">
        <H>Supprimer ce dossier ?</H>
        <p className="text-sm text-slate-600">{dossier?.sim?.compte || dossier?.snapshot_entree?.compte} disparaît de tous les indicateurs et rejoint la corbeille pendant 30 jours. Il reste restaurable pendant ce délai.</p>
        {onSortir ? <div className="rounded-xl bg-[#FBEFD5] p-3 text-sm text-[#7A4F0E]">Dossier perdu ou repoussé ? Utilisez plutôt{' '}
          <button type="button" className="font-bold underline" onClick={onSortir} data-testid="bb-supprimer-to-sortir">Clôturer le dossier</button>.</div> : null}
        <div><p className="text-xs text-slate-500 mb-1">Raison <b className="text-[#A1281D]">(obligatoire)</b></p>
          <select className={fieldCls} value={raison} onChange={e => setRaison(e.target.value)} data-testid="bb-supprimer-raison"><option value="">Choisir…</option>{RAISONS.map(r => <option key={r}>{r}</option>)}</select></div>
        {raison === 'Autre' && <textarea rows={2} className={fieldCls} placeholder="Précisez" value={comment} onChange={e => setComment(e.target.value)} />}
        <div className="flex justify-end gap-2"><button type="button" className={btnOutline} onClick={onClose}>Annuler</button>
          <button type="button" className="inline-flex items-center rounded-lg bg-[#C8372D] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#A1281D] disabled:opacity-50" disabled={!raison} onClick={submit} data-testid="bb-supprimer-submit">Supprimer</button></div>
      </DialogContent>
    </Dialog>
  );
};

export const CorbeilleDialog = ({ open, onClose, token, isSuperAdmin, onChanged }) => {
  const [items, setItems] = useState([]);
  const [purge, setPurge] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const load = () => bbFetch(token, '/bb/corbeille').then(setItems).catch(e => toast.error(e.message));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (d) => {
    try { await bbFetch(token, `/bb/dossiers/${d.id}/restaurer`, { method: 'POST' }); toast.success('Dossier restauré'); load(); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const doPurge = async () => {
    try { await bbFetch(token, `/bb/dossiers/${purge.id}?confirm=${encodeURIComponent(confirmName)}`, { method: 'DELETE' }); toast.success('Dossier supprimé définitivement'); setPurge(null); setConfirmName(''); load(); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="bb-corbeille-dialog">
        <H>Corbeille du Business Commercial</H>
        {items.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">La corbeille est vide.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-[10px] uppercase tracking-[0.12em] text-slate-500 text-left"><th className="py-2 pr-2">Compte</th><th className="pr-2">Commercial</th><th className="pr-2">Niveau avant</th><th className="pr-2">Supprimé le</th><th className="pr-2">Par</th><th className="pr-2">Raison</th><th className="pr-2">Purge dans</th><th /></tr></thead>
            <tbody>{items.map(d => (
              <tr key={d.id} className="border-t border-[#EEECE8]" data-testid={`bb-trash-${d.id}`}>
                <td className="py-2.5 pr-2 font-bold">{d.snapshot_entree?.compte}</td><td className="pr-2">{d.commercial_name}</td><td className="pr-2"><LevelBadge level={d.niveau} /></td>
                <td className="pr-2">{fmtDate(d.supprime_le)}</td><td className="pr-2">{d.supprime_par}</td><td className="pr-2">{d.motif_suppression}</td><td className="pr-2">{d.jours_restants} j</td>
                <td className="text-right whitespace-nowrap">
                  <button type="button" className={`${btnOutline} py-1.5 mr-1`} onClick={() => restore(d)} data-testid={`bb-restore-${d.id}`}>Restaurer</button>
                  {isSuperAdmin && <button type="button" className={`${btnDanger} py-1.5`} onClick={() => { setPurge(d); setConfirmName(''); }} data-testid={`bb-purge-${d.id}`}>Supprimer définitivement</button>}
                </td>
              </tr>))}</tbody>
          </table></div>
        )}
        {purge && (
          <div className="rounded-xl border border-[#C8372D] bg-[#FBE4E2] p-4 space-y-2" data-testid="bb-purge-confirm">
            <p className="text-sm font-bold text-[#A1281D]">Action irréversible. Tapez le nom du compte « {purge.snapshot_entree?.compte} » pour confirmer.</p>
            <input className={fieldCls} value={confirmName} onChange={e => setConfirmName(e.target.value)} data-testid="bb-purge-input" />
            <div className="flex justify-end gap-2"><button type="button" className={btnOutline} onClick={() => setPurge(null)}>Annuler</button>
              <button type="button" className={btnDanger} disabled={confirmName.trim().toLowerCase() !== (purge.snapshot_entree?.compte || '').toLowerCase()} onClick={doPurge} data-testid="bb-purge-submit">Confirmer la suppression définitive</button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PARAM_FIELDS = [
  ['seuil_alerte_sans_maj_jours', 'Alerte sans mise à jour (jours)', 'Au-delà, la ligne passe en rouge et le commercial est relancé chaque jour à 9 h'],
  ['delai_max_signature_jours', 'Délai maximal de signature (jours)', 'La signature visée ne peut pas dépasser ce délai'],
  ['objectif_contacts_semaine', 'Objectif contacts / semaine', 'Contacts adhérents ou directeurs attendus par semaine'],
  ['objectif_rdv_semaine', 'Objectif RDV / semaine', 'RDV obtenus attendus par semaine'],
  ['objectif_contrats_par_commercial', 'Objectif contrats par commercial', 'Contrats prospects à signer entre les deux dates ci-dessous'],
  ['date_debut_objectif', 'Début de la période d’objectif', 'Date de début du comptage', 'date'],
  ['date_fin_objectif', 'Fin de la période d’objectif', 'Date de fin du comptage', 'date'],
  ['objectif_contrats_prospects_annuel', 'Objectif annuel contrats prospects', 'Affiché dans la barre de la vue Direction'],
  ['echeance_declaration_jour', 'Jour d’échéance de la déclaration', '0 = lundi … 4 = vendredi'],
  ['echeance_declaration_heure', 'Heure d’échéance de la déclaration', 'Heure de Paris (ex. 17)'],
  ['duree_corbeille_jours', 'Durée de la corbeille (jours)', 'Purge automatique au-delà'],
  ['fenetre_simulations_a_examiner_jours', 'Fenêtre des simulations à examiner (jours)', 'Ancienneté maximale des simulations proposées en point'],
];

export const ParamsDialog = ({ open, onClose, token, onSaved }) => {
  const [p, setP] = useState(null);
  useEffect(() => { if (open) bbFetch(token, '/bb/params').then(d => setP(d.params)).catch(e => toast.error(e.message)); }, [open, token]);
  const save = async () => {
    const body = Object.fromEntries(Object.entries(p).map(([k, v]) => [k, PARAM_FIELDS.find(f => f[0] === k)?.[3] === 'date' ? v : Number(v)]));
    try { await bbFetch(token, '/bb/params', { method: 'PUT', body: JSON.stringify(body) }); toast.success('Paramètres enregistrés'); onSaved(); onClose(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="bb-params-dialog">
        <Eyebrow>Réservé Super admin</Eyebrow><H>Paramètres du Business Commercial</H>
        {p && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{PARAM_FIELDS.map(([k, l, help, type]) => (
          <div key={k}><p className="text-sm font-bold">{l}</p><p className="text-[11px] text-slate-500 mb-1">{help}</p>
            <input type={type || 'number'} className={fieldCls} value={p[k] ?? ''} onChange={e => setP(x => ({ ...x, [k]: e.target.value }))} data-testid={`bb-param-${k}`} /></div>
        ))}</div>}
        <div className="flex justify-end gap-2"><button type="button" className={btnOutline} onClick={onClose}>Annuler</button><button type="button" className={btnOrange} onClick={save} data-testid="bb-params-save">Enregistrer</button></div>
      </DialogContent>
    </Dialog>
  );
};
